import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const AlertFilters = ({ onFiltersChange, className = "" }) => {
  const [filters, setFilters] = useState({
    severity: 'all',
    status: 'all',
    device: 'all',
    dateRange: '24h',
    customStartDate: '',
    customEndDate: '',
    searchTerm: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const severityOptions = [
    { value: 'all', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'investigating', label: 'Investigating' },
    { value: 'resolved', label: 'Resolved' }
  ];

  const deviceOptions = [
    { value: 'all', label: 'All Devices' },
    { value: 'SL-001', label: 'Main Entrance (SL-001)' },
    { value: 'SL-002', label: 'Server Room (SL-002)' },
    { value: 'SL-003', label: 'Executive Office (SL-003)' },
    { value: 'SL-004', label: 'Conference Room A (SL-004)' },
    { value: 'SL-005', label: 'Storage Room (SL-005)' }
  ];

  const dateRangeOptions = [
    { value: '1h', label: 'Last Hour' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      severity: 'all',
      status: 'all',
      device: 'all',
      dateRange: '24h',
      customStartDate: '',
      customEndDate: '',
      searchTerm: ''
    };
    setFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.severity !== 'all') count++;
    if (filters?.status !== 'all') count++;
    if (filters?.device !== 'all') count++;
    if (filters?.dateRange !== '24h') count++;
    if (filters?.searchTerm) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className={`bg-card border border-border rounded-lg security-shadow-card ${className}`}>
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
            <Icon name="Filter" size={16} className="text-accent" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Alert Filters</h3>
            {activeFilterCount > 0 && (
              <p className="text-xs text-accent">{activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              iconName="X"
              className="text-muted-foreground text-xs"
            >
              Clear
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            className="text-muted-foreground"
          />
        </div>
      </div>
      {/* Quick Filters - Always Visible */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select
            label="Severity"
            options={severityOptions}
            value={filters?.severity}
            onChange={(value) => handleFilterChange('severity', value)}
          />
          <Select
            label="Status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
          />
          <Select
            label="Device"
            options={deviceOptions}
            value={filters?.device}
            onChange={(value) => handleFilterChange('device', value)}
          />
          <Select
            label="Time Range"
            options={dateRangeOptions}
            value={filters?.dateRange}
            onChange={(value) => handleFilterChange('dateRange', value)}
          />
        </div>
      </div>
      {/* Advanced Filters - Expandable */}
      <div className={`overflow-hidden security-transition ${isExpanded ? 'max-h-none' : 'max-h-0'}`}>
        <div className="p-4 space-y-4">
          {/* Search */}
          <Input
            label="Search Alerts"
            type="search"
            placeholder="Search by alert title, description, or device..."
            value={filters?.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e?.target?.value)}
          />

          {/* Custom Date Range */}
          {filters?.dateRange === 'custom' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="datetime-local"
                value={filters?.customStartDate}
                onChange={(e) => handleFilterChange('customStartDate', e?.target?.value)}
              />
              <Input
                label="End Date"
                type="datetime-local"
                value={filters?.customEndDate}
                onChange={(e) => handleFilterChange('customEndDate', e?.target?.value)}
              />
            </div>
          )}

          {/* Quick Filter Buttons */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Quick Filters</label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleFilterChange('severity', 'critical');
                  handleFilterChange('status', 'active');
                }}
                iconName="AlertTriangle"
                className="text-xs"
              >
                Critical Active
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleFilterChange('status', 'active');
                  handleFilterChange('dateRange', '1h');
                }}
                iconName="Clock"
                className="text-xs"
              >
                Recent Active
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleFilterChange('status', 'resolved');
                  handleFilterChange('dateRange', '7d');
                }}
                iconName="CheckCircle"
                className="text-xs"
              >
                Recently Resolved
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertFilters;